import React, { useCallback, useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { AddLocalWalletCredit, ClaimTopUp, CreateTopUpSession } from '../../Client/ManagementServiceClient';
import { useWallet } from '../../hooks/useWallet';
import './styles.css';

// Only used until the balance response arrives -- the server owns the real
// list, and it is the one that decides what a top-up may be. `price_usd` is
// charged; `credit_usd` is what lands in the balance, and is larger on the
// boosted packs.
const FALLBACK_TIERS = [
    { price_usd: 10, credit_usd: 10, bonus_percent: 0 },
    { price_usd: 50, credit_usd: 50, bonus_percent: 0 },
    { price_usd: 100, credit_usd: 105, bonus_percent: 5 },
    { price_usd: 500, credit_usd: 550, bonus_percent: 10 }
];

// Below this the pill turns amber: enough to be worth warning about, not so
// little that the warning only arrives once everything is already blocked.
const LOW_BALANCE_USD = 5;

const formatUsd = (value) => `$${Number(value || 0).toFixed(2)}`;


/**
 * Hosted-Checkout top-up. Money is never credited here -- Stripe redirects
 * back and the signed webhook is what actually moves the balance, so this
 * component's job ends at sending the user to Stripe.
 */
function TopUpDialog({ open, onClose, suggestedAmount }) {
    const { balance, localCreditEnabled, refresh } = useWallet();
    const tiers = balance?.topup_tiers?.length ? balance.topup_tiers : FALLBACK_TIERS;

    const [selected, setSelected] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setError('');
        setSubmitting(false);
        // Default to the smallest pack whose CREDIT covers the shortfall, so the
        // user is not left topping up just enough and immediately running dry.
        const covering = suggestedAmount > 0
            ? tiers.find((tier) => tier.credit_usd >= suggestedAmount)
            : null;
        setSelected((covering || tiers[0]).price_usd);
        // Re-running on every tiers identity change would fight the user's click.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, suggestedAmount]);

    const chosenTier = tiers.find((tier) => tier.price_usd === selected) || tiers[0];

    const handleSubmit = useCallback(async () => {
        if (!chosenTier || submitting) return;

        setSubmitting(true);
        setError('');
        try {
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            // Sent as what the user pays; the server derives what to credit.
            const { status, myJson } = await CreateTopUpSession(idToken, chosenTier.price_usd);

            if (status === 200 && myJson?.url) {
                window.location.href = myJson.url;
                return;
            }

            setError(myJson?.detail || 'Could not start the payment. Please try again.');
        } catch (e) {
            setError('Could not start the payment. Please try again.');
        }
        setSubmitting(false);
    }, [chosenTier, submitting]);

    // Local development has no Stripe webhook to credit a payment, so there is
    // no way to fund a wallet — and an unfunded wallet refuses every AI action.
    // The button is offered only where the server says it will honour it.
    const handleLocalCredit = useCallback(async () => {
        if (!chosenTier || submitting) return;

        setSubmitting(true);
        setError('');
        try {
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            const { status, myJson } = await AddLocalWalletCredit(idToken, chosenTier.price_usd);

            if (status === 200) {
                await refresh();
                onClose?.();
                return;
            }
            setError(myJson?.detail || 'Could not add local credit.');
        } catch (e) {
            setError('Could not add local credit.');
        }
        setSubmitting(false);
    }, [chosenTier, submitting, refresh, onClose]);

    // Recovers a payment Stripe took locally that no webhook could deliver --
    // including ones made before the redirect started claiming them, which are
    // otherwise charged and invisible.
    const handleClaim = useCallback(async () => {
        if (submitting) return;

        setSubmitting(true);
        setError('');
        try {
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            const { status, myJson } = await ClaimTopUp(idToken, null);

            if (status === 200) {
                await refresh();
                if (Number(myJson?.balance_usd || 0) > 0) {
                    onClose?.();
                    return;
                }
                setError('Stripe has no unclaimed payments for this account.');
            } else {
                setError(myJson?.detail || 'Could not check Stripe for payments.');
            }
        } catch (e) {
            setError('Could not check Stripe for payments.');
        }
        setSubmitting(false);
    }, [submitting, refresh, onClose]);

    if (!open) return null;

    return (
        <div className="wallet-modal-backdrop" onClick={onClose} role="presentation">
            <div
                className="wallet-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="wallet-topup-title"
            >
                <h2 id="wallet-topup-title">Add funds</h2>
                <p className="wallet-modal-sub">
                    Your balance pays for AI work as you use it. It never expires, and you can
                    ask for any unused balance back. Larger top-ups come with a bonus — $500
                    puts $550 in your balance.
                </p>

                <div className="wallet-amounts">
                    {tiers.map((tier) => (
                        <button
                            key={tier.price_usd}
                            type="button"
                            className={`wallet-amount ${chosenTier?.price_usd === tier.price_usd ? 'selected' : ''}`}
                            onClick={() => setSelected(tier.price_usd)}
                        >
                            <span className="wallet-amount-credit">{formatUsd(tier.price_usd)}</span>
                            {tier.bonus_percent > 0 && (
                                <>
                                    <span className="wallet-amount-price">
                                        get {formatUsd(tier.credit_usd)}
                                    </span>
                                    <span className="wallet-amount-badge">
                                        +{tier.bonus_percent}% bonus
                                    </span>
                                </>
                            )}
                        </button>
                    ))}
                </div>

                {error && <p className="wallet-error">{error}</p>}

                <div className="wallet-modal-actions">
                    <button type="button" className="wallet-btn-secondary" onClick={onClose} disabled={submitting}>
                        Cancel
                    </button>
                    {localCreditEnabled && (
                        <button
                            type="button"
                            className="wallet-btn-secondary"
                            onClick={handleLocalCredit}
                            disabled={!chosenTier || submitting}
                            title="Local development only: adds balance without taking a payment"
                        >
                            Add locally (dev)
                        </button>
                    )}
                    <button
                        type="button"
                        className="wallet-btn-primary"
                        onClick={handleSubmit}
                        disabled={!chosenTier || submitting}
                    >
                        {submitting
                            ? 'Opening Stripe…'
                            : `Pay ${formatUsd(chosenTier?.price_usd)}, get ${formatUsd(chosenTier?.credit_usd)}`}
                    </button>
                </div>

                <p className="wallet-modal-footnote">
                    Payment is handled by Stripe. Your card details never reach our servers.
                </p>

                {localCreditEnabled && (
                    <p className="wallet-modal-footnote">
                        Local development: Stripe cannot call this machine, so a payment credits on
                        the way back instead of by webhook.{' '}
                        <button
                            type="button"
                            className="wallet-link-btn"
                            onClick={handleClaim}
                            disabled={submitting}
                        >
                            Claim an earlier payment
                        </button>
                    </p>
                )}
            </div>
        </div>
    );
}


/**
 * The balance in the nav, plus the top-up entry point.
 *
 * Shown at all times rather than only when low: a prepaid product where the
 * user has to go looking for their balance is one where they discover it is
 * empty by being refused mid-task.
 */
function WalletBalance() {
    const { balance, availableUsd, loading, refresh } = useWallet();
    const [topUpOpen, setTopUpOpen] = useState(false);

    const openTopUp = useCallback(() => setTopUpOpen(true), []);
    const closeTopUp = useCallback(() => {
        setTopUpOpen(false);
        refresh();
    }, [refresh]);

    const isLow = !loading && availableUsd < LOW_BALANCE_USD;
    const isEmpty = !loading && availableUsd <= 0;
    const heldUsd = Number(balance?.reserved_usd || 0);

    return (
        <>
            <button
                type="button"
                className={`wallet-pill ${isEmpty ? 'empty' : isLow ? 'low' : ''}`}
                onClick={openTopUp}
                title={
                    heldUsd > 0
                        ? `${formatUsd(heldUsd)} is held for work in progress`
                        : 'Add funds to your balance'
                }
            >
                <span className="wallet-pill-label">Balance</span>
                <span className="wallet-pill-amount">
                    {loading ? '—' : formatUsd(availableUsd)}
                </span>
                <span className="wallet-pill-add" aria-hidden="true">+</span>
            </button>

            <TopUpDialog open={topUpOpen} onClose={closeTopUp} suggestedAmount={0} />
        </>
    );
}


/**
 * The shared out-of-funds dialog, raised by any AI action that gets a 402.
 *
 * Mounted once near the app root so every refusal reads the same and offers the
 * same next step, instead of each feature inventing its own error toast.
 */
function InsufficientFundsDialog() {
    const { fundingPrompt, dismissFundingPrompt, refresh } = useWallet();
    const [topUpOpen, setTopUpOpen] = useState(false);

    if (!fundingPrompt && !topUpOpen) return null;

    if (topUpOpen) {
        return (
            <TopUpDialog
                open
                suggestedAmount={fundingPrompt?.shortfallUsd || 0}
                onClose={() => { setTopUpOpen(false); dismissFundingPrompt(); refresh(); }}
            />
        );
    }

    return (
        <div className="wallet-modal-backdrop" onClick={dismissFundingPrompt} role="presentation">
            <div
                className="wallet-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="wallet-funds-title"
            >
                <h2 id="wallet-funds-title">Not enough balance</h2>
                <p className="wallet-modal-sub">{fundingPrompt.message}</p>

                <div className="wallet-funds-summary">
                    <div>
                        <span>Available</span>
                        <strong>{formatUsd(fundingPrompt.availableUsd)}</strong>
                    </div>
                    <div>
                        <span>This needs</span>
                        <strong>{formatUsd(fundingPrompt.requiredUsd)}</strong>
                    </div>
                </div>

                <p className="wallet-modal-footnote">
                    Nothing was started and nothing was charged.
                </p>

                <div className="wallet-modal-actions">
                    <button type="button" className="wallet-btn-secondary" onClick={dismissFundingPrompt}>
                        Not now
                    </button>
                    <button type="button" className="wallet-btn-primary" onClick={() => setTopUpOpen(true)}>
                        Add funds
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * "This will cost about $X — go ahead?", raised before an expensive AI action.
 *
 * Mounted once near the app root and driven entirely by the wallet context, so
 * a feature asks for confirmation by awaiting `confirmCost(...)` rather than by
 * building a dialog of its own. Only operations the SERVER called expensive get
 * here; cheap ones resolve without ever rendering this.
 *
 * Cancelling is the default outcome of every exit path — backdrop, Cancel — and
 * cancelling costs nothing, because nothing has been reserved yet.
 */
function CostConfirmDialog() {
    const { costPrompt, answerCostPrompt, availableUsd, loading } = useWallet();
    const [topUpOpen, setTopUpOpen] = useState(false);

    if (!costPrompt) return null;

    // Affordability is re-derived from the LIVE balance rather than read off the
    // quote, so a user who tops up from inside this dialog sees it unblock
    // instead of being told they are short of money they just added.
    const holdUsd = Number(costPrompt.hold_usd || 0);
    const affordable = loading
        ? costPrompt.sufficient !== false
        : Number(availableUsd || 0) >= holdUsd;
    const shortfallUsd = Math.max(0, holdUsd - Number(availableUsd || 0));

    /* The reservation is deliberately larger than the quote -- it is padded by
       how uncertain the cost is -- and it, not the quote, is what has to fit in
       the balance. Showing only the quote made this dialog contradict itself
       for every balance that landed between the two: "Up to $61.48, balance
       $62.32, you are $14.53 short". Both numbers are now on screen, so what
       the run needs is the number the user is being measured against.

       Compared at cent resolution because that is what is rendered: a hold
       fractions of a cent above the quote would otherwise print a second,
       identical-looking row. */
    const estimatedUsd = Number(costPrompt.estimated_usd || 0);
    const holdExceedsEstimate = Math.round(holdUsd * 100) > Math.round(estimatedUsd * 100);

    if (topUpOpen) {
        return (
            <TopUpDialog
                open
                suggestedAmount={shortfallUsd}
                // Topping up does not silently start the work: the user comes
                // back to the question they were asked.
                onClose={() => setTopUpOpen(false)}
            />
        );
    }

    const decline = () => answerCostPrompt(false);

    return (
        <div className="wallet-modal-backdrop" onClick={decline} role="presentation">
            <div
                className="wallet-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="wallet-cost-title"
            >
                <h2 id="wallet-cost-title">{costPrompt.label}</h2>
                <p className="wallet-modal-sub">
                    {costPrompt.note
                        ? `${costPrompt.note} `
                        : ''}
                    This is the most it can cost — you are charged for what it actually uses,
                    and the rest goes back to your balance.
                </p>

                <div className="wallet-funds-summary">
                    <div>
                        <span>Up to</span>
                        <strong>{formatUsd(estimatedUsd)}</strong>
                    </div>
                    {holdExceedsEstimate && (
                        <div>
                            <span>Held while it runs</span>
                            <strong>{formatUsd(holdUsd)}</strong>
                        </div>
                    )}
                    <div>
                        <span>Balance</span>
                        <strong>{formatUsd(loading ? costPrompt.available_usd : availableUsd)}</strong>
                    </div>
                </div>

                {!affordable && (
                    <p className="wallet-error">
                        {holdExceedsEstimate
                            ? `You are ${formatUsd(shortfallUsd)} short of the ${formatUsd(holdUsd)} held while this runs. `
                              + 'Whatever it does not use comes straight back.'
                            : `You are ${formatUsd(shortfallUsd)} short. Add funds to run this.`}
                    </p>
                )}

                <div className="wallet-modal-actions">
                    <button type="button" className="wallet-btn-secondary" onClick={decline}>
                        Cancel
                    </button>
                    {affordable ? (
                        <button
                            type="button"
                            className="wallet-btn-primary"
                            onClick={() => answerCostPrompt(true)}
                        >
                            Run it
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="wallet-btn-primary"
                            onClick={() => setTopUpOpen(true)}
                        >
                            Add funds
                        </button>
                    )}
                </div>

                <p className="wallet-modal-footnote">
                    {`Anything over ${formatUsd(costPrompt.threshold_usd)} asks first. Nothing is charged until the work runs.`}
                </p>
            </div>
        </div>
    );
}

export { WalletBalance, TopUpDialog, InsufficientFundsDialog, CostConfirmDialog };
